import React, { useMemo, useState } from "react";

const WEEKDAY_NAMES = [
  "SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT",
];

// 대한민국 법정공휴일 (2026년 기준, 매년 갱신 필요 — 음력 기반 공휴일은 해마다 날짜가 바뀜)
const HOLIDAYS_2026 = {
  "2026-01-01": "New Year's Day",
  "2026-02-16": "Seollal",
  "2026-02-17": "Seollal",
  "2026-02-18": "Seollal",
  "2026-03-01": "Independence Day",
  "2026-03-02": "Holiday",
  "2026-05-05": "Children's Day",
  "2026-05-24": "Buddha's Day",
  "2026-05-25": "Holiday",
  "2026-06-06": "Memorial Day",
  "2026-07-17": "Constitution Day",
  "2026-08-15": "Liberation Day",
  "2026-08-17": "Holiday",
  "2026-09-24": "Chuseok",
  "2026-09-25": "Chuseok",
  "2026-09-26": "Chuseok",
  "2026-10-03": "Foundation Day",
  "2026-10-05": "Holiday",
  "2026-10-09": "Hangeul Day",
  "2026-12-25": "Christmas",
  "2027-01-01": "New Year's Day",
};

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dateKey(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, delta) {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1);
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * 달력 6주(42칸) 만들기:
 * - gridStart: 해당 달 1일이 속한 주의 Sunday부터 시작
 * - 42칸 채움 (이전/다음 달 포함)
 */
function buildCalendarGrid(viewDate) {
  const first = startOfMonth(viewDate);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // Sunday 기준으로 뒤로

  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

const today = new Date();
today.setHours(0, 0, 0, 0);

const maxDate = new Date();
maxDate.setMonth(maxDate.getMonth() + 6);
maxDate.setHours(0, 0, 0, 0);

function DayCell({ d, month, year, selectedDate, onSelect }) {
  const inMonth = d.getMonth() === month && d.getFullYear() === year;
  const selected = isSameDay(d, selectedDate) && inMonth;
  const isToday = isSameDay(d, today);
  const isPast = d < today;
  const isBeyond = d > maxDate;
  const isDisabled = isPast || isBeyond || !inMonth;
  const dow = d.getDay();
  const holidayName = HOLIDAYS_2026[dateKey(d)];
  const isHoliday = !!holidayName && inMonth;

  let colorClass;
  if (selected) {
    colorClass = "bg-[#E8876A] text-white shadow-md";
  } else if (isDisabled) {
    colorClass = "text-gray-200 cursor-not-allowed";
  } else if (dow === 0 || isHoliday) {
    colorClass = "text-red-400 hover:bg-[#E8876A]/10";
  } else if (dow === 6) {
    colorClass = "text-[#3b559d] hover:bg-[#E8876A]/10";
  } else {
    colorClass = "text-gray-800 hover:bg-[#E8876A]/10";
  }

  const caption = isToday ? "TODAY" : inMonth ? holidayName : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(d)}
      disabled={isDisabled}
      className="flex flex-col items-center gap-0.5 py-[2px]"
    >
      <div
        className={[
          "w-12 h-12 md:w-10 md:h-10 rounded-full flex items-center justify-center text-[16px] md:text-[14px] font-bold transition-all duration-150",
          colorClass,
        ].join(" ")}
      >
        {d.getDate()}
      </div>
      <div className="h-3 w-full px-0.5 flex items-center justify-center">
        {caption && (
          <span
            className={[
              "text-[8px] font-bold uppercase leading-none truncate",
              isToday ? "text-[#E8876A]" : "text-red-400",
            ].join(" ")}
          >
            {caption}
          </span>
        )}
      </div>
    </button>
  );
}

export default function Calendar({
  initialDate = new Date(),
  onChange,
  embedded = false,
}) {
  const [viewDate, setViewDate] = useState(startOfMonth(initialDate));
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const grid = useMemo(() => buildCalendarGrid(viewDate), [viewDate]);

  const month = viewDate.getMonth();
  const year = viewDate.getFullYear();

  const canGoNext = addMonths(viewDate, 1) <= maxDate;
  const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();

  const handlePrev = () => setViewDate((d) => addMonths(d, -1));
  const handleNext = () => {
    if (!canGoNext) return;
    setViewDate((d) => addMonths(d, 1));
  };
  const handleToday = () => setViewDate(startOfMonth(today));

  const handleSelect = (d) => {
    if (d < today || d > maxDate) return;
    setSelectedDate(d);
    onChange?.(d);
  };

  return (
    <div className={`relative w-full bg-white font-poppins ${embedded ? "py-0" : "py-6"}`}>
      <div className="max-w-[900px] mx-auto md:px-0">
        <div className={[
          "px-10 py-6 md:px-4 md:py-5",
          embedded
            ? "border-0 shadow-none"
            : "rounded-[28px] md:rounded-[0px] border border-gray-100 shadow-[0_10px_40px_-14px_rgba(0,0,0,0.15)]",
        ].join(" ")}>

          <div className="relative flex flex-row w-full items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleToday}
              disabled={isCurrentMonth}
              className={`absolute right-0 text-[12px] font-bold uppercase tracking-wide transition-colors ${
                isCurrentMonth ? "text-gray-200 cursor-not-allowed" : "text-[#E8876A] hover:underline"
              }`}
            >
              Today
            </button>

            <button
              type="button"
              onClick={handlePrev}
              className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-gray-400 hover:bg-[#E8876A]/10 hover:text-[#E8876A] transition-colors"
              aria-label="Prev month"
            >
              <div className="w-3 h-3 border-l-2 border-b-2 border-current rotate-45" />
            </button>

            <div className="text-[26px] font-extrabold tracking-tight tabular-nums text-[#2C2C2A]">
              {year}.{pad2(month + 1)}
            </div>

            <button
              type="button"
              onClick={handleNext}
              disabled={!canGoNext}
              className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                canGoNext ? "text-gray-400 hover:bg-[#E8876A]/10 hover:text-[#E8876A]" : "text-gray-200 cursor-not-allowed"
              }`}
              aria-label="Next month"
            >
              <div className="w-3 h-3 border-r-2 border-b-2 border-current -rotate-45" />
            </button>
          </div>

          <div className="mt-5">
            <div className="grid grid-cols-7 text-center border-b border-gray-100">
              {WEEKDAY_NAMES.map((w, i) => (
                <div
                  key={w}
                  className={[
                    "pb-2 text-[11px] font-bold tracking-wider",
                    i === 0 ? "text-red-300" : i === 6 ? "text-[#3b559d]/60" : "text-gray-400",
                  ].join(" ")}
                >
                  {w}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-y-1 pt-2 text-center">
              {grid.map((d) => (
                <DayCell
                  key={d.toISOString()}
                  d={d}
                  month={month}
                  year={year}
                  selectedDate={selectedDate}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-[13px]">
            <span className="text-gray-400 font-medium">Selected date</span>
            <span className="font-bold text-[#E8876A]">
              {selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
          </div>

        </div>
      </div>
    </div>
  );
}
